import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { Newspaper, Calendar, User, ArrowRight, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const getText = (key) => {
    const texts = {
      title: {
        fr: 'Blog & Actualités',
        en: 'Blog & News',
        uk: 'Блог та новини',
        ru: 'Блог и новости',
        it: 'Blog & Notizie',
        ro: 'Blog & Știri',
        ka: 'ბლოგი და სიახლეები'
      },
      subtitle: {
        fr: 'Découvrez nos derniers articles sur la santé, le bien-être et les médecines douces',
        en: 'Discover our latest articles on health, wellness and natural medicine',
        uk: 'Відкрийте наші останні статті про здоров\'я, благополуччя та натуральну медицину',
        ru: 'Откройте наши последние статьи о здоровье, благополучии и натуральной медицине',
        it: 'Scopri i nostri ultimi articoli sulla salute, il benessere e la medicina naturale',
        ro: 'Descoperă ultimele noastre articole despre sănătate, bunăstare și medicină naturală',
        ka: 'აღმოაჩინეთ ჩვენი უახლესი სტატიები ჯანმრთელობის, კეთილდღეობისა და ბუნებრივი მედიცინის შესახებ'
      },
      searchPlaceholder: {
        fr: 'Rechercher un article...',
        en: 'Search articles...',
        uk: 'Пошук статей...',
        ru: 'Поиск статей...',
        it: 'Cerca articoli...',
        ro: 'Caută articole...',
        ka: 'სტატიების ძებნა...'
      },
      readArticle: {
        fr: 'Lire l\'article',
        en: 'Read article',
        uk: 'Читати статтю',
        ru: 'Читать статью',
        it: 'Leggi articolo',
        ro: 'Citește articolul',
        ka: 'სტატიის წაკითხვა'
      },
      close: {
        fr: 'Fermer',
        en: 'Close',
        uk: 'Закрити',
        ru: 'Закрыть',
        it: 'Chiudi',
        ro: 'Închide',
        ka: 'დახურვა'
      },
      noArticles: {
        fr: 'Aucun article trouvé',
        en: 'No articles found',
        uk: 'Статей не знайдено',
        ru: 'Статей не найдено',
        it: 'Nessun articolo trovato',
        ro: 'Niciun articol găsit',
        ka: 'სტატიები ვერ მოიძებნა'
      },
      comingSoon: {
        fr: 'Articles à venir bientôt',
        en: 'Articles coming soon',
        uk: 'Статті незабаром',
        ru: 'Статьи скоро',
        it: 'Articoli in arrivo',
        ro: 'Articole în curând',
        ka: 'სტატიები მალე'
      },
      stayTuned: {
        fr: 'Restez informé',
        en: 'Stay informed',
        uk: 'Будьте в курсі',
        ru: 'Будьте в курсе',
        it: 'Resta informato',
        ro: 'Rămâi informat',
        ka: 'იყავით ინფორმირებული'
      },
      newsletter: {
        fr: 'Inscrivez-vous à notre newsletter pour recevoir les dernières actualités',
        en: 'Subscribe to our newsletter to receive the latest news',
        uk: 'Підпишіться на нашу розсилку, щоб отримувати останні новини',
        ru: 'Подпишитесь на нашу рассылку, чтобы получать последние новости',
        it: 'Iscriviti alla nostra newsletter per ricevere le ultime notizie',
        ro: 'Abonează-te la newsletter-ul nostru pentru a primi ultimele noutăți',
        ka: 'გამოიწერეთ ჩვენი სიახლეების ბიულეტენი უახლესი ამბების მისაღებად'
      }
    };
    return texts[key]?.[language] || texts[key]?.['fr'] || key;
  };

  const categories = [
    { id: 'all', name: { fr: 'Tous', en: 'All', uk: 'Усі', ru: 'Все', it: 'Tutti', ro: 'Toate', ka: 'ყველა' } },
    { id: 'wellness', name: { fr: 'Bien-être', en: 'Wellness', uk: 'Благополуччя', ru: 'Благополучие', it: 'Benessere', ro: 'Bunăstare', ka: 'კეთილდღეობა' } },
    { id: 'health', name: { fr: 'Santé', en: 'Health', uk: 'Здоров\'я', ru: 'Здоровье', it: 'Salute', ro: 'Sănătate', ka: 'ჯანმრთელობა' } },
    { id: 'testimonials', name: { fr: 'Témoignages', en: 'Testimonials', uk: 'Відгуки', ru: 'Отзывы', it: 'Testimonianze', ro: 'Mărturii', ka: 'მოწმობები' } },
    { id: 'news', name: { fr: 'Actualités', en: 'News', uk: 'Новини', ru: 'Новости', it: 'Attualità', ro: 'Actualități', ka: 'სიახლეები' } }
  ];

  // Default articles with full content
  const defaultArticles = [
    {
      id: 1,
      slug: 'bienfaits-naturopathie',
      title: {
        fr: 'Les bienfaits de la naturopathie pour les expatriés',
        en: 'Benefits of Naturopathy for Expatriates'
      },
      excerpt: {
        fr: 'Découvrez comment la naturopathie peut aider les expatriés à gérer le stress de l\'adaptation et maintenir leur équilibre.',
        en: 'Discover how naturopathy can help expatriates manage adaptation stress and maintain their balance.'
      },
      content: {
        fr: `<h2>La naturopathie : une alliée pour les expatriés</h2>
<p>S'expatrier est une aventure enrichissante, mais elle peut aussi être source de stress important. Changement de climat, décalage horaire, nouvelle alimentation, éloignement de la famille... Autant de facteurs qui peuvent perturber notre équilibre.</p>

<h3>Qu'est-ce que la naturopathie ?</h3>
<p>La naturopathie est une médecine traditionnelle qui vise à maintenir et/ou rétablir la santé par des moyens naturels. Elle s'appuie sur plusieurs techniques :</p>
<ul>
<li><strong>L'alimentation</strong> : adapter son régime au nouveau pays tout en préservant ses besoins nutritionnels</li>
<li><strong>L'exercice physique</strong> : maintenir une activité régulière malgré les changements de rythme</li>
<li><strong>La gestion du stress</strong> : techniques de relaxation et de respiration</li>
<li><strong>La phytothérapie</strong> : utilisation des plantes pour soutenir l'organisme</li>
</ul>

<h3>Les bienfaits pour les expatriés</h3>
<p>Pour les personnes vivant à l'étranger, la naturopathie offre plusieurs avantages :</p>
<ul>
<li>Aide à l'adaptation au décalage horaire et aux nouveaux rythmes</li>
<li>Soutien du système immunitaire face aux nouveaux environnements</li>
<li>Gestion naturelle du stress et de l'anxiété liés à l'expatriation</li>
<li>Conseils nutritionnels adaptés aux produits locaux disponibles</li>
<li>Accompagnement dans les changements de saison et de climat</li>
</ul>

<h3>Consulter un naturopathe à distance</h3>
<p>Grâce à la téléconsultation, il est désormais possible de consulter un naturopathe francophone depuis n'importe où dans le monde. Les consultations en visio permettent un suivi personnalisé, avec des conseils adaptés à votre situation géographique et à vos besoins spécifiques.</p>

<p><em>Sur Opora Vie, retrouvez des naturopathes certifiés qui parlent votre langue et comprennent les défis de l'expatriation.</em></p>`,
        en: `<h2>Naturopathy: An ally for expatriates</h2>
<p>Expatriation is an enriching adventure, but it can also be a source of significant stress. Climate change, jet lag, new diet, distance from family... All factors that can disturb our balance.</p>

<h3>What is naturopathy?</h3>
<p>Naturopathy is a traditional medicine that aims to maintain and/or restore health through natural means. It relies on several techniques:</p>
<ul>
<li><strong>Diet</strong>: adapting your diet to the new country while preserving your nutritional needs</li>
<li><strong>Physical exercise</strong>: maintaining regular activity despite changes in rhythm</li>
<li><strong>Stress management</strong>: relaxation and breathing techniques</li>
<li><strong>Herbal medicine</strong>: using plants to support the body</li>
</ul>

<h3>Benefits for expatriates</h3>
<p>For people living abroad, naturopathy offers several advantages:</p>
<ul>
<li>Help with jet lag adaptation and new rhythms</li>
<li>Immune system support in new environments</li>
<li>Natural management of stress and anxiety related to expatriation</li>
<li>Nutritional advice adapted to local products available</li>
<li>Support through seasonal and climate changes</li>
</ul>

<h3>Consulting a naturopath remotely</h3>
<p>Thanks to teleconsultation, it is now possible to consult a French-speaking naturopath from anywhere in the world. Video consultations allow personalized follow-up, with advice adapted to your geographical situation and specific needs.</p>

<p><em>On Opora Vie, find certified naturopaths who speak your language and understand the challenges of expatriation.</em></p>`
      },
      category: 'wellness',
      author: 'Dr. Marie Laurent',
      date: '2026-03-15',
      readTime: '5 min'
    },
    {
      id: 2,
      slug: 'telemedecine-internationale',
      title: {
        fr: 'La télémédecine : révolution pour les patients internationaux',
        en: 'Telemedicine: Revolution for International Patients'
      },
      excerpt: {
        fr: 'Comment la visioconférence transforme l\'accès aux soins pour les patients à travers le monde.',
        en: 'How video conferencing is transforming access to care for patients around the world.'
      },
      content: {
        fr: `<h2>La télémédecine : une révolution pour l'accès aux soins</h2>
<p>La pandémie de COVID-19 a accéléré l'adoption de la télémédecine, mais pour les patients internationaux, cette technologie représente bien plus qu'une alternative temporaire : c'est une véritable révolution dans l'accès aux soins.</p>

<h3>Les avantages de la téléconsultation</h3>
<ul>
<li><strong>Accessibilité</strong> : Consultez depuis n'importe où dans le monde, sans contrainte géographique</li>
<li><strong>Gain de temps</strong> : Plus besoin de voyager pour voir un spécialiste</li>
<li><strong>Continuité des soins</strong> : Gardez le contact avec votre praticien même en déplacement</li>
<li><strong>Choix élargi</strong> : Accédez à des praticiens spécialisés qui parlent votre langue</li>
<li><strong>Coût réduit</strong> : Économisez sur les frais de transport et d'hébergement</li>
</ul>

<h3>Comment se déroule une téléconsultation ?</h3>
<p>La téléconsultation sur Opora Vie est simple et sécurisée :</p>
<ol>
<li>Choisissez votre praticien et réservez un créneau</li>
<li>Recevez un lien de visioconférence par email</li>
<li>Connectez-vous à l'heure du rendez-vous depuis votre ordinateur ou smartphone</li>
<li>Consultez en toute confidentialité via notre plateforme sécurisée Jitsi</li>
<li>Recevez vos recommandations par email après la consultation</li>
</ol>

<h3>Pour quelles consultations ?</h3>
<p>La téléconsultation est particulièrement adaptée pour :</p>
<ul>
<li>Les consultations de suivi</li>
<li>Les conseils en naturopathie et nutrition</li>
<li>Les séances de sophrologie et relaxation</li>
<li>Les consultations psychologiques</li>
<li>Les bilans de bien-être</li>
</ul>

<p><em>Découvrez nos praticiens proposant la téléconsultation sur Opora Vie.</em></p>`,
        en: `<h2>Telemedicine: A revolution for access to care</h2>
<p>The COVID-19 pandemic has accelerated the adoption of telemedicine, but for international patients, this technology represents much more than a temporary alternative: it is a true revolution in access to care.</p>

<h3>Benefits of teleconsultation</h3>
<ul>
<li><strong>Accessibility</strong>: Consult from anywhere in the world, without geographical constraints</li>
<li><strong>Time saving</strong>: No need to travel to see a specialist</li>
<li><strong>Continuity of care</strong>: Stay in touch with your practitioner even when traveling</li>
<li><strong>Expanded choice</strong>: Access specialized practitioners who speak your language</li>
<li><strong>Reduced cost</strong>: Save on transportation and accommodation costs</li>
</ul>

<h3>How does a teleconsultation work?</h3>
<p>Teleconsultation on Opora Vie is simple and secure:</p>
<ol>
<li>Choose your practitioner and book a slot</li>
<li>Receive a video conference link by email</li>
<li>Log in at the appointment time from your computer or smartphone</li>
<li>Consult confidentially via our secure Jitsi platform</li>
<li>Receive your recommendations by email after the consultation</li>
</ol>

<h3>For which consultations?</h3>
<p>Teleconsultation is particularly suitable for:</p>
<ul>
<li>Follow-up consultations</li>
<li>Naturopathy and nutrition advice</li>
<li>Sophrology and relaxation sessions</li>
<li>Psychological consultations</li>
<li>Wellness assessments</li>
</ul>

<p><em>Discover our practitioners offering teleconsultation on Opora Vie.</em></p>`
      },
      category: 'health',
      author: 'Équipe Opora Vie',
      date: '2026-03-10',
      readTime: '4 min'
    },
    {
      id: 3,
      slug: 'temoignage-olga-ukraine',
      title: {
        fr: 'Témoignage : Olga, de Kiev à Paris',
        en: 'Testimonial: Olga, from Kyiv to Paris'
      },
      excerpt: {
        fr: 'Le parcours d\'Olga qui a trouvé un sophrologue ukrainien à Paris pour l\'aider dans sa transition.',
        en: 'Olga\'s journey finding a Ukrainian sophrologist in Paris to help with her transition.'
      },
      content: {
        fr: `<h2>« J'avais besoin de quelqu'un qui comprenne ce que je vivais »</h2>
<p><em>Olga, 34 ans, est arrivée en France en 2022 après avoir quitté Kiev. Elle nous raconte comment Opora Vie l'a aidée à trouver un accompagnement adapté.</em></p>

<h3>Le choc de l'expatriation</h3>
<p>« Quand je suis arrivée à Paris, j'étais complètement perdue. Je ne parlais pas français, je ne connaissais personne, et je portais le poids de tout ce que j'avais laissé derrière moi. J'avais des insomnies, des angoisses... Je savais que j'avais besoin d'aide. »</p>

<h3>La barrière de la langue</h3>
<p>« J'ai d'abord essayé de consulter un psychologue français, mais c'était très difficile. Comment exprimer des émotions aussi profondes dans une langue qu'on ne maîtrise pas ? J'avais l'impression de ne pas pouvoir vraiment me faire comprendre. »</p>

<h3>La découverte d'Opora Vie</h3>
<p>« Une amie ukrainienne m'a parlé d'Opora Vie. J'y ai trouvé Natalia, une sophrologue ukrainienne installée à Paris. Pouvoir parler dans ma langue maternelle a tout changé. Elle comprenait non seulement mes mots, mais aussi mon contexte culturel, mes références, mes non-dits. »</p>

<h3>Un accompagnement sur mesure</h3>
<p>« Natalia m'a proposé un programme de sophrologie adapté à ma situation. Les séances m'ont appris à gérer mon anxiété, à mieux dormir, à retrouver confiance en moi. Elle m'a aussi donné des outils pratiques que je peux utiliser au quotidien. »</p>

<h3>Aujourd'hui</h3>
<p>« Deux ans après, je me sens beaucoup mieux. J'ai appris le français, j'ai trouvé un travail, et je continue à voir Natalia de temps en temps, maintenant en téléconsultation car elle a déménagé. C'est formidable de pouvoir garder ce lien malgré la distance. »</p>

<p><strong>Le conseil d'Olga :</strong> « N'hésitez pas à chercher un praticien qui parle votre langue. C'est tellement important de pouvoir s'exprimer librement, surtout quand on traverse des moments difficiles. »</p>`,
        en: `<h2>"I needed someone who understood what I was going through"</h2>
<p><em>Olga, 34, arrived in France in 2022 after leaving Kyiv. She tells us how Opora Vie helped her find suitable support.</em></p>

<h3>The shock of expatriation</h3>
<p>"When I arrived in Paris, I was completely lost. I didn't speak French, I didn't know anyone, and I was carrying the weight of everything I had left behind. I had insomnia, anxiety... I knew I needed help."</p>

<h3>The language barrier</h3>
<p>"I first tried to consult a French psychologist, but it was very difficult. How can you express such deep emotions in a language you don't master? I felt like I couldn't really make myself understood."</p>

<h3>Discovering Opora Vie</h3>
<p>"A Ukrainian friend told me about Opora Vie. I found Natalia there, a Ukrainian sophrologist based in Paris. Being able to speak in my native language changed everything. She understood not only my words, but also my cultural context, my references, my unspoken feelings."</p>

<h3>Personalized support</h3>
<p>"Natalia offered me a sophrology program adapted to my situation. The sessions taught me how to manage my anxiety, sleep better, and regain confidence. She also gave me practical tools that I can use daily."</p>

<h3>Today</h3>
<p>"Two years later, I feel much better. I learned French, found a job, and I continue to see Natalia from time to time, now via teleconsultation since she moved. It's wonderful to be able to keep this connection despite the distance."</p>

<p><strong>Olga's advice:</strong> "Don't hesitate to look for a practitioner who speaks your language. It's so important to be able to express yourself freely, especially when going through difficult times."</p>`
      },
      category: 'testimonials',
      author: 'Rédaction',
      date: '2026-03-05',
      readTime: '6 min'
    },
    {
      id: 4,
      slug: 'nouvelles-specialites-2026',
      title: {
        fr: 'Nouvelles spécialités disponibles en 2026',
        en: 'New Specialties Available in 2026'
      },
      excerpt: {
        fr: 'Opora Vie élargit son réseau avec de nouvelles disciplines de médecines douces.',
        en: 'Opora Vie expands its network with new natural medicine disciplines.'
      },
      content: {
        fr: `<h2>Opora Vie s'enrichit de nouvelles spécialités</h2>
<p>En ce début d'année 2026, nous sommes heureux d'annoncer l'arrivée de nouvelles disciplines sur notre plateforme, pour répondre toujours mieux aux besoins de nos utilisateurs internationaux.</p>

<h3>Les nouvelles spécialités</h3>
<ul>
<li><strong>Médecine Traditionnelle Chinoise</strong> : Acupuncture, pharmacopée chinoise, Qi Gong thérapeutique</li>
<li><strong>Ayurveda</strong> : Consultations et conseils selon la médecine traditionnelle indienne</li>
<li><strong>Ostéopathie</strong> : Praticiens certifiés pour des consultations en cabinet</li>
<li><strong>Art-thérapie</strong> : Accompagnement par l'expression artistique</li>
<li><strong>Hypnothérapie</strong> : Hypnose thérapeutique pour divers troubles</li>
</ul>

<h3>De nouveaux pays couverts</h3>
<p>Notre réseau s'étend également géographiquement avec l'arrivée de praticiens dans :</p>
<ul>
<li>La Géorgie (Tbilissi, Batoumi)</li>
<li>La Moldavie (Chișinău)</li>
<li>Les pays baltes (Lituanie, Lettonie, Estonie)</li>
<li>La Pologne (Varsovie, Cracovie)</li>
</ul>

<h3>Toujours plus de langues</h3>
<p>Pour mieux servir notre communauté, la plateforme est maintenant disponible en 8 langues : français, anglais, allemand, italien, russe, roumain, ukrainien et géorgien.</p>

<h3>Rejoignez notre réseau</h3>
<p>Vous êtes praticien en médecines douces et souhaitez rejoindre Opora Vie ? Notre réseau est ouvert aux professionnels certifiés souhaitant accompagner une clientèle internationale. Contactez-nous pour en savoir plus sur les conditions d'inscription.</p>

<p><em>Merci à tous nos utilisateurs pour leur confiance. Ensemble, rendons le bien-être accessible à tous, partout dans le monde.</em></p>`,
        en: `<h2>Opora Vie welcomes new specialties</h2>
<p>At the beginning of 2026, we are pleased to announce the arrival of new disciplines on our platform, to better meet the needs of our international users.</p>

<h3>New specialties</h3>
<ul>
<li><strong>Traditional Chinese Medicine</strong>: Acupuncture, Chinese pharmacopoeia, therapeutic Qi Gong</li>
<li><strong>Ayurveda</strong>: Consultations and advice according to traditional Indian medicine</li>
<li><strong>Osteopathy</strong>: Certified practitioners for office consultations</li>
<li><strong>Art therapy</strong>: Support through artistic expression</li>
<li><strong>Hypnotherapy</strong>: Therapeutic hypnosis for various disorders</li>
</ul>

<h3>New countries covered</h3>
<p>Our network is also expanding geographically with the arrival of practitioners in:</p>
<ul>
<li>Georgia (Tbilisi, Batumi)</li>
<li>Moldova (Chișinău)</li>
<li>Baltic countries (Lithuania, Latvia, Estonia)</li>
<li>Poland (Warsaw, Krakow)</li>
</ul>

<h3>More languages</h3>
<p>To better serve our community, the platform is now available in 8 languages: French, English, German, Italian, Russian, Romanian, Ukrainian and Georgian.</p>

<h3>Join our network</h3>
<p>Are you a natural medicine practitioner and want to join Opora Vie? Our network is open to certified professionals wishing to support an international clientele. Contact us to learn more about registration conditions.</p>

<p><em>Thank you to all our users for their trust. Together, let's make wellness accessible to everyone, everywhere in the world.</em></p>`
      },
      category: 'news',
      author: 'Équipe Opora Vie',
      date: '2026-02-28',
      readTime: '3 min'
    }
  ];

  // Fetch articles from API on mount
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles`);
        if (res.data && res.data.length > 0) {
          const apiArticles = res.data.map(article => ({
            id: article.id,
            slug: article.slug,
            title: {
              fr: article.title_fr || '',
              en: article.title_en || article.title_fr || ''
            },
            excerpt: {
              fr: article.excerpt_fr || '',
              en: article.excerpt_en || article.excerpt_fr || ''
            },
            content: {
              fr: article.content_fr || article.excerpt_fr || '',
              en: article.content_en || article.content_fr || article.excerpt_fr || ''
            },
            category: article.category,
            author: article.author || 'Équipe Opora Vie',
            date: article.created_at ? article.created_at.split('T')[0] : '2026-01-01',
            readTime: article.read_time || '5 min'
          }));
          setArticles(apiArticles);
        } else {
          setArticles(defaultArticles);
        }
      } catch (error) {
        console.log('Using default articles');
        setArticles(defaultArticles);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const filteredArticles = articles.filter(article => {
    const titleText = article.title[language] || article.title.fr || '';
    const excerptText = article.excerpt[language] || article.excerpt.fr || '';
    const matchesSearch = searchTerm === '' || 
      titleText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      excerptText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const locale = language === 'uk' ? 'uk-UA' : language === 'ru' ? 'ru-RU' : language === 'en' ? 'en-US' : 'fr-FR';
    return date.toLocaleDateString(locale, options);
  };

  // Close modal when clicking outside or pressing Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSelectedArticle(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 py-16">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <Newspaper className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {getText('title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {getText('subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="container-app py-8">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={getText('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'bg-sage-500 hover:bg-sage-600' : ''}
              >
                {cat.name[language] || cat.name.fr}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="container-app py-8">
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map(article => (
              <article 
                key={article.id}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-soft hover:shadow-md transition-all group cursor-pointer"
                onClick={() => setSelectedArticle(article)}
              >
                <div className="aspect-video bg-gradient-to-br from-sage-100 to-sand-100 flex items-center justify-center">
                  <Newspaper className="w-16 h-16 text-sage-300" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-sage-100 text-sage-700">
                      {categories.find(c => c.id === article.category)?.name[language] || article.category}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-sage-600 transition-colors line-clamp-2">
                    {article.title[language] || article.title.fr}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {article.excerpt[language] || article.excerpt.fr}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {article.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.date)}
                    </div>
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full group-hover:bg-sage-50 group-hover:border-sage-200"
                  >
                    {getText('readArticle')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Newspaper className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-medium mb-2">{getText('noArticles')}</h3>
            <p className="text-muted-foreground">{getText('comingSoon')}</p>
          </div>
        )}
      </div>

      {/* Newsletter CTA */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-12">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            {getText('stayTuned')}
          </h2>
          <p className="text-amber-100 mb-6 max-w-lg mx-auto">
            {getText('newsletter')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input 
              type="email" 
              placeholder="Email" 
              className="bg-white/90 border-0 rounded-xl"
            />
            <Button className="bg-white text-amber-600 hover:bg-amber-50 whitespace-nowrap">
              {language === 'uk' ? 'Підписатися' : language === 'ru' ? 'Подписаться' : language === 'en' ? 'Subscribe' : 'S\'inscrire'}
            </Button>
          </div>
        </div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-sand-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-sage-100 text-sage-700">
                  {categories.find(c => c.id === selectedArticle.category)?.name[language] || selectedArticle.category}
                </span>
                <span className="text-sm text-muted-foreground">{selectedArticle.readTime}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedArticle(null)}
                className="hover:bg-sand-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                {selectedArticle.title[language] || selectedArticle.title.fr}
              </h2>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b border-sand-200">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {selectedArticle.author}
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedArticle.date)}
                </div>
              </div>
              
              {/* Article Content */}
              <div 
                className="prose prose-sage max-w-none
                  prose-headings:font-heading prose-headings:text-foreground
                  prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:mt-5 prose-h3:mb-3
                  prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-4 prose-li:text-muted-foreground
                  prose-strong:text-foreground
                  prose-em:text-sage-600"
                dangerouslySetInnerHTML={{ 
                  __html: selectedArticle.content[language] || selectedArticle.content.fr || selectedArticle.excerpt[language] || selectedArticle.excerpt.fr
                }}
              />
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-sand-200 p-4">
              <Button 
                onClick={() => setSelectedArticle(null)}
                className="w-full bg-sage-500 hover:bg-sage-600"
              >
                {getText('close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
